import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { addDays, format, subDays } from "date-fns"
import { RefreshCw, Ship } from "lucide-react"
import { useCallback, useState } from "react"
import { z } from "zod"

import { CalendarService } from "@/client"
import { CalendarDateNavigation } from "@/components/Calendar/CalendarDateNavigation.tsx"
import { CalendarFreshnessIndicator } from "@/components/Calendar/CalendarFreshnessIndicator.tsx"
import { OfflineBanner } from "@/components/Calendar/OfflineBanner.tsx"
import { WaterTaxiDetailModal } from "@/components/Calendar/WaterTaxiDetailModal"
import {
  type CalendarTrip,
  getTimePeriod,
  TripCardSkeleton,
  WaterTaxiTripCard,
} from "@/components/Calendar/WaterTaxiTripCard"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useOnlineStatus } from "@/hooks/useOnlineStatus"
import { usePullToRefresh } from "@/hooks/usePullToRefresh"
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation"
import { parseLocalDate } from "@/lib/utils"

// Search params schema for URL state
const searchParamsSchema = z.object({
  date: z.string().optional(),
})

export const Route = createFileRoute("/_layout/calendar/taxi")({
  component: WaterTaxiCalendarPage,
  validateSearch: searchParamsSchema,
  staticData: {
    pageTitle: "Water Taxi Schedule",
    pageIcon: Ship,
    pageIconClassName: "text-primary",
  },
  head: () => ({
    meta: [
      {
        title: "Water Taxi Calendar - Broken Islands Booking",
      },
    ],
  }),
})

/**
 * Empty state for the calendar when no trips exist.
 */
function EmptyCalendarState({
  selectedDate,
  onGoToToday,
}: {
  selectedDate: Date
  onGoToToday: () => void
}) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <Ship className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No bookings for this day</h3>
        <p className="text-muted-foreground mb-4">
          There are no water taxi trips scheduled for{" "}
          {format(selectedDate, "MMMM d, yyyy")}
        </p>
        <Button variant="outline" onClick={onGoToToday}>
          <Ship className="mr-2 h-4 w-4" />
          Go to Today
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Error state for the calendar.
 */
function CalendarErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="border-destructive">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <RefreshCw className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-medium mb-2">Unable to load calendar</h3>
        <p className="text-muted-foreground mb-4">
          There was a problem loading the water taxi schedule.
        </p>
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Try Again
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Daily summary header component.
 */
function DailySummary({
  summary,
}: {
  summary: {
    scheduled_runs: number
    custom_pickups: number
    total_passengers: number
  }
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-lg">
            <Ship className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Scheduled Runs
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {summary.scheduled_runs}
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div className="bg-accent p-2 rounded-lg">
            <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-accent-foreground" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Custom Pickups
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {summary.custom_pickups}
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div className="bg-green-500/10 p-2 rounded-lg">
            <UsersIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Total Passengers
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {summary.total_passengers}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Icon components for DailySummary to avoid unused import warnings.
 */
function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <title>Location</title>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <title>Users</title>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

/**
 * Main Water Taxi Calendar page component.
 */
function WaterTaxiCalendarPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const searchParams = Route.useSearch()
  const [selectedTrip, setSelectedTrip] = useState<CalendarTrip | null>(null)
  const { isOnline } = useOnlineStatus()

  // Parse selected date from URL or use today
  const selectedDate = searchParams.date
    ? parseLocalDate(searchParams.date)
    : new Date()
  const selectedDateString = format(selectedDate, "yyyy-MM-dd")

  // Fetch calendar data with polling
  const {
    data: calendarData,
    isLoading,
    isError,
    refetch,
    dataUpdatedAt,
    isRefetching,
  } = useQuery({
    queryKey: ["calendar", "water-taxi", selectedDateString],
    queryFn: () =>
      CalendarService.getWaterTaxiCalendar({
        requestedDate: selectedDateString,
      }),
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  })

  // Navigation handlers
  const handlePrevDay = useCallback(() => {
    const prevDate = subDays(selectedDate, 1)
    navigate({
      to: ".",
      search: { date: format(prevDate, "yyyy-MM-dd") },
    })
  }, [selectedDate, navigate])

  const handleNextDay = useCallback(() => {
    const nextDate = addDays(selectedDate, 1)
    navigate({
      to: ".",
      search: { date: format(nextDate, "yyyy-MM-dd") },
    })
  }, [selectedDate, navigate])

  const handleGoToToday = useCallback(() => {
    navigate({
      to: ".",
      search: {},
    })
  }, [navigate])

  const handleDateSelect = useCallback(
    (date: Date) => {
      navigate({
        to: ".",
        search: { date: format(date, "yyyy-MM-dd") },
      })
    },
    [navigate],
  )

  // Swipe navigation
  const swipeHandlers = useSwipeNavigation({
    onSwipeLeft: handleNextDay,
    onSwipeRight: handlePrevDay,
    minDistance: 50,
  })

  // Pull to refresh
  const pullHandlers = usePullToRefresh({
    onRefresh: async () => {
      await refetch()
    },
    threshold: 60,
    enabled: isOnline,
  })

  // Get calendar data safely
  const calendar = calendarData?.data as Record<string, unknown> | undefined
  const trips = (calendar?.trips ?? []) as CalendarTrip[] | undefined
  const summary = calendar?.summary as
    | {
        scheduled_runs: number
        custom_pickups: number
        total_passengers: number
      }
    | undefined

  // Group trips by time period
  const tripsByPeriod = (trips ?? []).reduce(
    (acc, trip) => {
      const period = getTimePeriod(trip.time)
      if (!acc[period]) {
        acc[period] = []
      }
      acc[period].push(trip)
      return acc
    },
    {} as Record<string, CalendarTrip[]>,
  )

  // Sort trips within each period by time
  Object.keys(tripsByPeriod).forEach((period) => {
    tripsByPeriod[period].sort((a, b) => a.time.localeCompare(b.time))
  })

  // Combined touch handlers - pull takes priority when at top of scroll
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchStart(e)
      swipeHandlers.onTouchStart(e)
    },
    [pullHandlers, swipeHandlers],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchMove(e)
      swipeHandlers.onTouchMove(e)
    },
    [pullHandlers, swipeHandlers],
  )

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchEnd(e)
      swipeHandlers.onTouchEnd()
    },
    [pullHandlers, swipeHandlers],
  )

  return (
    <div
      ref={pullHandlers.containerRef}
      className="container py-6 touch-pan-y overscroll-y-contain"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Offline Banner */}
      {!isOnline && <OfflineBanner className="mb-4" />}

      {/* Pull to refresh indicator */}
      {pullHandlers.isPulling && (
        <div
          className="flex justify-center py-2 transition-transform"
          style={{
            transform: `translateY(${Math.min(pullHandlers.pullDistance, 60)}px)`,
          }}
        >
          <RefreshCw
            className={`h-5 w-5 text-muted-foreground ${
              pullHandlers.pullDistance >= 60 ? "animate-spin" : ""
            }`}
          />
        </div>
      )}

      {/* Detail Modal */}
      <WaterTaxiDetailModal
        trip={selectedTrip}
        open={!!selectedTrip}
        onOpenChange={(open) => {
          if (!open) setSelectedTrip(null)
        }}
      />

      {/* Date navigation bar */}
      <CalendarDateNavigation
        selectedDate={selectedDate}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        onGoToToday={handleGoToToday}
        onDateSelect={handleDateSelect}
        className="mb-6"
      />

      {/* Content */}
      {isLoading ? (
        <div className="space-y-6">
          <DailySummary
            summary={{
              scheduled_runs: 0,
              custom_pickups: 0,
              total_passengers: 0,
            }}
          />
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Morning</h3>
            {Array.from({ length: 3 }).map((_, i) => (
              <TripCardSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : isError ? (
        <CalendarErrorState onRetry={() => refetch()} />
      ) : !trips?.length ? (
        <EmptyCalendarState
          selectedDate={selectedDate}
          onGoToToday={handleGoToToday}
        />
      ) : (
        <>
          {/* Daily summary */}
          {summary && <DailySummary summary={summary} />}

          {/* Freshness indicator */}
          {dataUpdatedAt && (
            <div className="mb-4">
              <CalendarFreshnessIndicator
                dataUpdatedAt={dataUpdatedAt}
                onRefresh={() => refetch()}
                isRefetching={isRefetching}
              />
            </div>
          )}

          {/* Trips grouped by time period */}
          {Object.entries(tripsByPeriod).map(([period, periodTrips]) => (
            <div key={period} className="mb-6">
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                {period === "Morning" && "☀️"}
                {period === "Afternoon" && "🌤️"}
                {period === "Evening" && "🌅"}
                {period}
              </h3>
              <div className="space-y-3">
                {periodTrips.map((trip) => (
                  <WaterTaxiTripCard
                    key={trip.trip_id}
                    trip={trip}
                    onClick={() => setSelectedTrip(trip)}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  )
}
